import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { StyleCreator } from "../../helpers/stylecreator";
import { FormStyleCollection as formStyles } from "../../styles/form";
import { Callback, isEmpty, isMobile } from "../../utils/Util";
import { JWTDecode, TargetPayApi } from "../../utils/UtilTarget";
import TerminalDropdown from "../../common/components/TerminalDropdown";
import { PaymentScreenHeader } from "./PaymentScreenHeader";
import { getSignatureErrorMessage } from "./signatureErrorMessages";
import {
  TargetPayBaseResponse,
  TargetPayShopperSignatureResponse,
  TargetPaySignatureFailureResponse,
  TargetPaySignatureRequest
} from "../../types/types";
import LoadingSubmitButton from "../../common/components/LoaderSubmitButton";
import {
  useGetDealerCredentialQuery,
  useGetTransactionDealerIdQuery,
  useRetrieveShopperSignatureQuery,
  useValidateSignatureQuery,
  validateSignature
} from "../../services/API/signatureApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/store";
import {
  resetState,
  setErrorMessage,
  setIsProcessing,
  setSignatureFetched,
  setSignatureResponse,
  setSignatureToken,
  setTerminalId,
  setTerminalList,
  setValidatedSignatureData,
  setDataFetched,
  setEmergepayIsSandBox,
  setIsCancelled
} from "../../features/nonPaymentSignature/signatureSlice";
import { datadogLogs } from "@datadog/browser-logs";

export const SignatureRequest = () => {
  const classes = StyleCreator.makeStyles(formStyles.all);
  const dispatch = useDispatch();
  const [isQueryReady, setIsQueryReady] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  const dataLogger = datadogLogs.logger;

  const {
    signatureToken,
    terminalId,
    dataFetched,
    signatureFetched,
    terminalList,
    errorMessage,
    signatureResponse,
    isProcessing,
    validatedSignatureData,
    EmergepayIsSandBox,
    IsCancelled
  } = useSelector((state: RootState) => state.signature);

  useEffect(() => {
    resetForm();
    getQueryParams();
    window.addEventListener("message", EmergePayResponse);
  }, []);

  const getQueryParams = () => {
    var urlParams = new URLSearchParams(window.location.search);
    const signature_token = urlParams.get("signature_token");
    const access_token = urlParams.get("access_token");
    const refresh_token = urlParams.get("refresh_token");
    let obj: any = new Object();
    setAuthTokens(access_token, refresh_token, signature_token);
  };

  const EmergePayResponse = (event: any) => {
    if (isEmpty(event.data) || isEmpty(event.data.isApproved)) return;

    try {
      let urlParams = new URLSearchParams(window.location.search);
      event.data.isMobile = urlParams.get("ismobile");
      Callback(event.data);
    } catch (error) {
      console.error("Error: Signature.EmergePayResponse: " + error);
    }
  };
  const setAuthTokens = (access_token, refresh_token, signature_token) => {
    if (!isEmpty(refresh_token))
      localStorage.setItem("refresh_token", refresh_token);
    if (!isEmpty(access_token))
      localStorage.setItem("access_token", access_token);
    readToken(signature_token);
  };
  const readToken = async signature_token => {
    try {
      if (!isEmpty(signature_token)) {
        dispatch(setSignatureToken(signature_token));
        if (signature_token.length > 50) {
          getNonce(signature_token);
          setIsQueryReady(true); // Set the flag to true to allow the query to run
        }
      } else {
        dispatch(setErrorMessage("No Signature token provided"));
      }
    } catch (error) {
      dispatch(setErrorMessage("Invalid/expired Signature token"));
    }
  };

  // RTK Query to validate targetPay signature token
  const { data, isError, isFetching } = useValidateSignatureQuery(
    { signatureToken: signatureToken },
    {
      skip: isEmpty(signatureToken) || !isQueryReady, // Skip the query if token is empty
      refetchOnMountOrArgChange: true
    }
  );

  // RTK Query to the status of customer's signature from database
  const {
    data: retrieveShopperSignatureData,
    isError: retrieveShopperSignatureError
  } = useRetrieveShopperSignatureQuery(
    {
      dealerId: validatedSignatureData.dealerId,
      customerId: validatedSignatureData.customerID
    },
    {
      skip:
        !validatedSignatureData?.dealerId ||
        !validatedSignatureData?.customerID ||
        !isQueryReady, // Skip the query if dealer id or customer id is empty
      refetchOnMountOrArgChange: true
    }
  );

  // RTK Query hook to get DealerId based on token
  const {
    data: transactionData,
    error: transactionError
  } = useGetTransactionDealerIdQuery(signatureToken, {
    skip: !shouldFetch || !signatureToken || signatureToken.length > 50, // Skip the query if the token length is greater than 50
    refetchOnMountOrArgChange: true
  });

  // RTK Query hook to get dealer credentials
  const { data: dealerData, error: dealerError } = useGetDealerCredentialQuery(
    transactionData?.DealerId,
    {
      skip: !transactionData?.DealerId, // Skip if DealerId is not available
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    if (data && !isError && !isFetching) {
      dispatch(setValidatedSignatureData(data));
      fetchTerminals(data.dealerId);
      dispatch(validateSignature.util.resetApiState());
    } else if (isError || data === null) {
      resetForm();
      dispatch(setErrorMessage("Invalid/expired Signature token"));
    }
  }, [data, isError]);

  useEffect(() => {
    if (retrieveShopperSignatureData) {
      if (retrieveShopperSignatureData.status === "Cancelled") {
        dispatch(setIsCancelled(true));
      }
    }
  }, [retrieveShopperSignatureData, dispatch]);

  useEffect(() => {
    if (dealerData) {
      // Dispatch the result of the second API call
      dispatch(setEmergepayIsSandBox(dealerData.isSandbox ? 1 : 0));
      dispatch(setDataFetched(true));
      dispatch(validateSignature.util.resetApiState());
    } else if (dealerError || transactionError) {
      dispatch(setErrorMessage("Error Occured file fetching dealer settings"));
    }
  }, [dealerData, dispatch]);

  const fetchTerminals = async dealer_id => {
    try {
      if (!isNaN(dealer_id)) {
        const response = await TargetPayApi(
          "StoreTerminals",
          { dealerId: dealer_id },
          "get",
          "PerseusPay",
          true
        );

        const status = response.response?.status;
        const isClientError = status >= 400 && status < 500;
        const isServerError = status >= 500 && status < 600;

        if (isClientError || isServerError) {
          const message = isServerError
            ? "Unable to load terminals due to a server error. Please try again later or contact support if the issue persists."
            : "Unable to load terminals. Please refresh the page or contact support if the issue persists.";

          toast.error(message, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 3000,
            toastId: "store-terminals-error"
          });

          dataLogger.error(
            `StoreTerminals ${
              isServerError ? "5xx" : "4xx"
            } error for DealerId: ${dealer_id}. Status: ${status} - ${
              response.response?.statusText
            }`
          );

          dispatch(setErrorMessage("No Terminal Assigned"));
          return;
        }

        const filteredTerminals = (response?.data || []).filter(
          (terminal: any) =>
            terminal.dealerLocationId === dealer_id ||
            terminal.dealerLocationId === 0
        );
        if (isEmpty(filteredTerminals))
          dispatch(setErrorMessage("No Terminal Assigned"));
        else dispatch(setTerminalList(filteredTerminals));
      }
    } catch (error) {
      toast.error("Error fetching terminals");
    }
  };

  const resetForm = () => {
    dispatch(resetState());
    setShouldFetch(true);
  };

  const getNonce = (signatureToken: string) => {
    let nonce = JWTDecode(signatureToken, "Nonce");
    if (!isEmpty(nonce)) {
      nonce = nonce
        .split("")
        .reverse()
        .join("");
      sessionStorage.setItem("nonce", nonce);
    }
  };

  const isSignatureResponse = (
    response: TargetPayBaseResponse
  ): response is TargetPayShopperSignatureResponse => {
    // Axios (^1.7+) exposes a top-level `.status` on AxiosError objects that
    // mirrors the HTTP status code (e.g. 504). A `status !== undefined` check
    // alone therefore also matches failed/timed-out requests, not just real
    // TargetPayShopperSignatureResponse payloads. Use axios's own type guard
    // to rule those out, then require the business `status` field (a string,
    // per the TargetPayShopperSignatureResponse contract) to be present.
    if (!response || axios.isAxiosError(response)) return false;
    return typeof (response as { status?: unknown }).status === "string";
  };
  const isFailureResponse = (
    response: TargetPayBaseResponse
  ): response is TargetPaySignatureFailureResponse => {
    const status = (response as {
      response?: { data?: { status?: unknown } };
    })?.response?.data?.status;
    return status !== undefined && typeof status !== "number";
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const request: TargetPaySignatureRequest = {
      POIID: terminalId,
      dealerId: validatedSignatureData.dealerId,
      machineName: validatedSignatureData.machineName,
      token: signatureToken,
      userId: validatedSignatureData.userId,
      customerID: validatedSignatureData.customerID,
      customerName: validatedSignatureData.customerName,
      transactionReference: validatedSignatureData.transactionReference,
      cashierID: validatedSignatureData.cashierID,
      externalTransId: validatedSignatureData.externalTransId
    };

    try {
      dispatch(setSignatureFetched(false));
      dispatch(setIsProcessing(true));
      const res: TargetPayBaseResponse = await TargetPayApi(
        "RequestShopperSignature",
        request,
        "post",
        "AdyenSignature",
        true,
        false
      );
      if (isSignatureResponse(res)) {
        SetSignatureData(res);
      } else if (isFailureResponse(res)) {
        if (res.response.data.status === "Error")
          dispatch(setErrorMessage(res.response.data.additionalInfo));
       else SetSignatureData(res.response.data);
      } else {
        dispatch(setErrorMessage("Unexpected error, Please try again later"));
      }
    } catch (error) {
      toast.error("Unable to proceed the request");
    } finally {
      dispatch(setIsProcessing(false));
    }
  };

  const SetSignatureData = (response: TargetPayShopperSignatureResponse) => {
    response.additionalInfo =
      response.isApproved && isEmpty(response.transactionResponse.signature)
        ? "No Signature provided by shopper on terminal"
        : response.additionalInfo;
    dispatch(setSignatureResponse(response));
    Callback(response, validatedSignatureData.hostUrl);

    const isSuccess = response.isApproved && response.status === "Success";
    toast.dismiss();
    if (isSuccess) {
      toast.success("Status: " + response.status, {
        position: toast.POSITION.TOP_CENTER
      });
    } else {
      toast.error(
        "Status: " +
          response.status +
          (!isEmpty(response.additionalInfo)
            ? " - Reason: " + response.additionalInfo
            : ""),
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: false,
          closeOnClick: true
        }
      );
    }
  };

  const renderUI = () => {
    if (!isEmpty(errorMessage)) {
      return (
        <Box style={{ height: "100vh" }}>
          <Box
            display="flex"
            alignItems="center"
            style={{
              border: "1px solid #E5E7E9",
              padding: "5px 10px",
              marginTop: "20px",
              color: "red",
              backgroundColor: "#F8F9F9",
              borderRadius: "10px"
            }}
          >
            <h3>{errorMessage}</h3>
          </Box>
        </Box>
      );
    } else if (!dataFetched) {
      return (
        <Box display="flex" alignItems="center" style={{ height: "100%" }}>
          <CircularProgress />
          <span style={{ marginLeft: "10px" }}>
            Please wait. Processing.....
          </span>
        </Box>
      );
    } else if (
      dataFetched &&
      terminalList.length > 0 &&
      signatureToken.length > 50
    ) {
      return (
        <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 760 }}>
          <Box sx={{ width: "100%", maxWidth: 760 }}>
            <PaymentScreenHeader
              invoiceId={null}
              requestedAmount={null}
              platformFee={null}
              totalAmount={null}
              platformFeeFlag={false}
              amountLabel=""
              status={signatureResponse.status || null}
              customerName={validatedSignatureData.customerName ?? null}
              customerId={validatedSignatureData.customerID ?? null}
              customerEmail={null}
              customerPhone={null}
              isProcessing={isProcessing}
              callbackPosted={signatureFetched}
              isSaveCardFlow={true}
              processingMessage="Please do not close or refresh this page until the signature request has been completed."
            />
            <Tabs
              value="CaptureSignature"
              aria-label="basic tabs example"
              TabIndicatorProps={{
                style: { height: 3, borderRadius: "3px 3px 0 0" }
              }}
              sx={{
                minHeight: 44,
                "& .MuiTabs-flexContainer": {
                  gap: 0.5
                },
                "& .MuiTab-root": {
                  minHeight: 44,
                  px: 1.5,
                  fontSize: "0.78rem",
                  fontWeight: 500,
                  letterSpacing: 0.5,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  transition: "color 0.15s",
                  minWidth: "unset"
                },
                "& .MuiTab-root.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 700
                },
                "& .MuiTab-root.Mui-disabled": {
                  opacity: 0.4
                }
              }}
            >
              <Tab label="Capture Signature" value="CaptureSignature" />
            </Tabs>
            <Box
              className={classes.signatureBox}
              style={{
                minWidth: !isMobile() ? "400px" : "100px"
              }}
            >
              <Box style={{ border: "0px solid red" }}>
                <Box style={{ marginTop: "20px" }}>
                  <TerminalDropdown
                    terminalList={terminalList}
                    terminalId={terminalId}
                    setTerminalId={id => dispatch(setTerminalId(id))}
                    disabled={
                      isProcessing ||
                      IsCancelled ||
                      signatureResponse.status === "Cancelled"
                    }
                    defaultTerminalId={
                      !isEmpty(
                        JSON.parse(validatedSignatureData.dealerInfo)
                          .UserDefaultPOIID
                      )
                        ? JSON.parse(validatedSignatureData.dealerInfo)
                            .UserDefaultPOIID
                        : validatedSignatureData.deviceName
                    }
                    label="Terminal"
                    size="small"
                  />

                  <Box style={{ marginTop: "20px", textAlign: "right" }}>
                    <LoadingSubmitButton
                      loading={isProcessing}
                      disabled={
                        terminalId === "0" ||
                        terminalId === "" ||
                        terminalList.length === 0 ||
                        IsCancelled ||
                        signatureResponse.status === "Cancelled"
                      }
                      onClick={handleSubmit}
                      label={"Capture Signature"}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              className={classes.paymentBoxManual}
              style={{ textAlign: "right" }}
            ></Box>
          </Box>
        </form>
      );
    } else if (
      !isEmpty(signatureToken) &&
      EmergepayIsSandBox > -1 &&
      signatureToken.length < 50
    ) {
      return (
        <Box>
          <iframe
            style={{ border: "0px solid red", width: "700px", height: "570px" }}
            title="static_html"
            src={`emergePay.html?isSandBox=${EmergepayIsSandBox}&transactionToken=${signatureToken}`}
          ></iframe>
        </Box>
      );
    }
    return null; // Return null if no conditions are met
  };

  return (
    <Box>
      <Helmet>
        <title> Request Signature </title>
      </Helmet>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ padding: "10px" }}
      >
        {renderUI()}
      </Box>
    </Box>
  );
};
